import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { documentService } from '@/services/documentService';
import { userService } from '@/services/userService';
import { API_BASE_URL } from '@/services/api';
import { filterDocuments, groupDocuments, hasEditAccess, isExcelDoc, isWordDoc } from '../utils/documentHelpers';

export const useDocuments = (user) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formatFilter, setFormatFilter] = useState('all');
  // Updated Halimunnisa 09-06-2026
  // Added a more flexible filters state to allow for multiple filter types (e.g., date range) and easy extension in the future, improving the filtering capabilities and user experience.
  const [filters, setFilters] = useState({ date: 'all', fromDate: '', toDate: '' });
  const [expandedSection, setExpandedSection] = useState(location.state?.section || 'excel');
  const [isStatsExpanded, setIsStatsExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docToDeleteId, setDocToDeleteId] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [isRelatedToCatalog, setIsRelatedToCatalog] = useState(false);
  const [createType, setCreateType] = useState('excel');
  const [createTitle, setCreateTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editFile, setEditFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [uploading, setUploading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await documentService.getDocuments();
      let normalDocs = res.success ? res.data : [];

      try {
        const token = localStorage.getItem('blum_token');
        const designRes = await fetch(`${API_BASE_URL}/api/design`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const designData = await designRes.json();
        if (designData.success) {
          const designMediaDocs = [];
          designData.data.leads.forEach(lead => {
            const isAssignedToUser = !user || user.role === 'owner' || user.role === 'admin' ||
              String(lead.assignedTo?._id || lead.assignedTo) === String(user._id) ||
              String(lead.telecaller?._id || lead.telecaller) === String(user._id);
            if (isAssignedToUser && lead.designMedia && lead.designMedia.length > 0) {
              lead.designMedia.forEach(media => {
                if (media.filename?.endsWith('.xlsx') || media.filename?.endsWith('.xls')) {
                  designMediaDocs.push({
                    _id: media.fileId,
                    title: media.filename,
                    fileName: media.filename,
                    fileUrl: `/api/design/media/${media.fileId}`,
                    fileType: media.mimetype,
                    size: media.size,
                    uploadedBy: { name: 'Verve Design' },
                    updatedAt: media.uploadedAt || lead.updatedAt,
                    createdAt: media.uploadedAt || lead.updatedAt,
                    isVerveDesignMedia: true,
                    designId: lead._id,
                    verveId: (lead.customId || '').toUpperCase(),
                    permissions: [],
                  });
                }
              });
            }
          });
          normalDocs = [...normalDocs, ...designMediaDocs];
        }
      } catch (err) {
        console.error("Failed to fetch design media", err);
      }

      setDocuments(normalDocs);
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await userService.getUsers({ limit: 100, status: 'Active' });
      if (res.success) setAllUsers(res.data);
    } catch {
      /* optional */
    }
  }, []);

  useEffect(() => {
    if (showPermissionModal) fetchUsers();
  }, [showPermissionModal, fetchUsers]);
// Updated Halimunnisa 09-06-2026
// Memoized filtered and grouped documents to optimize performance when applying search and filters, ensuring smooth user experience even with larger datasets.
  const filteredDocs = useMemo(
    () => filterDocuments(documents, search, formatFilter, filters),
    [documents, search, formatFilter, filters]
  );

  const groupedDocs = useMemo(() => groupDocuments(filteredDocs), [filteredDocs]);

  const statsCounts = useMemo(() => {
    const all = groupDocuments(documents);
    return {
      total: documents.length,
      excel: all.excel.length,
      word: all.word.length,
      pdf: all.pdf.length,
      drawings: all.drawings.length,
      catalog: all.catalog.length,
    };
  }, [documents]);

  const openDocument = useCallback(
    (doc) => {
      const isActualExcel = doc.fileType?.includes('spreadsheetml') || doc.fileType?.includes('excel') || doc.fileName?.toLowerCase().endsWith('.xlsx') || doc.fileName?.toLowerCase().endsWith('.xls') || doc.fileName?.toLowerCase().endsWith('.csv');
      const isActualWord = doc.fileType?.includes('wordprocessingml') || doc.fileType?.includes('word') || doc.fileName?.toLowerCase().endsWith('.docx') || doc.fileName?.toLowerCase().endsWith('.doc');

      if (doc.isVerveDesignMedia) {
        navigate(`/design/media/edit/${doc.designId}/${doc._id}`);
      } else if (isActualExcel) {
        navigate(`/documents/edit/${doc._id}`);
      } else if (isActualWord) {
        navigate(`/documents/edit-word/${doc._id}`);
      } else {
        setSelectedDoc(doc);
        setEditTitle(doc.title);
        setShowPreviewModal(true);
      }
    },
    [navigate]
  );

  const handleDownloadFile = async (doc) => {
    try {
      toast.loading('Downloading...', { id: 'download' });
      const url = `${API_BASE_URL}${doc.fileUrl}`;
      const token = localStorage.getItem('blum_token');
      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = doc.fileName || doc.title || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Download complete', { id: 'download' });
    } catch (error) {
      toast.error('Failed to download file', { id: 'download' });
    }
  };
// Updated Halimunnisa 09-06-2026
// Added export functionality to allow users to export their documents list with applied filters, enhancing data management and usability for users with larger document libraries.
  const handleExport = async () => {
    if (isExporting || !filteredDocs || filteredDocs.length === 0) return;
    try {
      setIsExporting(true);
      const { exportToExcel } = await import("@/utils/exportUtils");
      const exportData = filteredDocs.map(doc => {
        const sizeFormatter = (bytes) => {
          if (!bytes) return "—";
          const k = 1024;
          const sizes = ['Bytes', 'KB', 'MB', 'GB'];
          const i = Math.floor(Math.log(bytes) / Math.log(k));
          return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        };
        const dateFmt = (d) => d ? new Date(d).toLocaleDateString('en-IN') : "—";
        return {
          "Document Name": doc.title || doc.fileName || "—",
          "Verve ID": doc.verveId || "—",
          "Type": doc.isVerveDesignMedia ? "Design Media" : (doc.fileType || "—"),
          "Size": sizeFormatter(doc.size),
          "Uploaded By": doc.uploadedBy?.name || "—",
          "Last Modified": dateFmt(doc.updatedAt || doc.createdAt),
          "Created At": dateFmt(doc.createdAt)
        };
      });
      await exportToExcel(exportData, `Documents_Export_${new Date().toISOString().split('T')[0]}`);
    } catch (err) {
      console.error("Failed to export documents:", err);
      toast.error("Failed to export documents");
    } finally {
      setIsExporting(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('title', uploadTitle || uploadFile.name);
    formData.append('isPublic', 'false');
    if (uploadCategory) formData.append('category', uploadCategory);
    if (uploadNotes) formData.append('notes', uploadNotes);
    if (isRelatedToCatalog) formData.append('isRelatedToCatalog', 'true');
    try {
      setUploading(true);
      const res = await documentService.uploadDocument(formData);
      if (res.success) {
        toast.success('Document uploaded successfully');
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadTitle('');
        setUploadCategory('');
        setUploadNotes('');
        setIsRelatedToCatalog(false);
        fetchDocuments();
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateBlank = async (e) => {
    e.preventDefault();
    if (!createTitle) return;
    try {
      setCreating(true);
      const res = await documentService.createBlankDocument({ title: createTitle, type: createType });
      if (res.success) {
        toast.success(`New ${createType === 'excel' ? 'Excel' : 'Word'} document created`);
        setShowCreateModal(false);
        setCreateTitle('');
        fetchDocuments();
      }
    } catch {
      toast.error('Failed to create document');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = (id) => {
    setDocToDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!docToDeleteId) return;
    const docToDelete = documents.find(d => d._id === docToDeleteId);
    try {
      setDeleting(true);
      let res;
      if (docToDelete?.isVerveDesignMedia) {
        const token = localStorage.getItem('blum_token');
        const deleteRes = await fetch(`${API_BASE_URL}/api/design/${docToDelete.designId}/${docToDeleteId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        res = await deleteRes.json();
      } else {
        res = await documentService.deleteDocument(docToDeleteId);
      }
      
      if (res.success) {
        toast.success('Document deleted');
        fetchDocuments();
      }
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDocToDeleteId(null);
    }
  };

  const handleUpdate = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!selectedDoc) return;
    const formData = new FormData();
    if (editTitle) formData.append('title', editTitle);
    if (editFile) formData.append('file', editFile);
    try {
      const res = await documentService.updateDocument(selectedDoc._id, formData);
      if (res.success) {
        toast.success('Document updated');
        setEditFile(null);
        fetchDocuments();
      }
    } catch {
      toast.error('Update failed');
    }
  };

  const handleUpdatePermissions = async (data) => {
    if (!selectedDoc) return null;
    try {
      let res;
      if (selectedDoc.isVerveDesignMedia) {
        const token = localStorage.getItem('blum_token');
        const updateRes = await fetch(`${API_BASE_URL}/api/design/media/permissions/${selectedDoc.designId}/${selectedDoc._id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
        const json = await updateRes.json();
        res = { success: json.success, data: json.data };
      } else {
        res = await documentService.updatePermissions(selectedDoc._id, data);
      }
      return res;
    } catch {
      toast.error('Failed to update permissions');
      return { success: false };
    }
  };

  const handleAddUser = async (userId) => {
    if (!selectedDoc) return;
    try {
      const newPermissions = [
        ...(selectedDoc.permissions || []),
        { user: { _id: userId, name: '', role: '' }, canView: true, canEdit: false },
      ];
      const res = await handleUpdatePermissions({ permissions: newPermissions });
      if (res?.success) {
        toast.success('Access granted');
        setSelectedDoc({ ...selectedDoc, permissions: res.data.permissions });
        fetchDocuments();
      }
    } catch {
      toast.error('Failed to add access');
    }
  };

  const canEdit = (doc) => {
    if (doc?.isVerveDesignMedia) return true; // Assuming owners/admins can edit design media, or just allow it
    return hasEditAccess(doc, user);
  };

  useEffect(() => {
    if (selectedDoc && isExcelDoc(selectedDoc)) {
      const fetchExcel = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}${selectedDoc.fileUrl}?t=${Date.now()}`);
          const arrayBuffer = await response.arrayBuffer();
          const data = new Uint8Array(arrayBuffer);
          const workbook = window.XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = window.XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          setExcelData(jsonData);
        } catch {
          setExcelData(null);
        }
      };
      fetchExcel();
    } else {
      setExcelData(null);
    }
  }, [selectedDoc]);

  useEffect(() => {
    if (selectedDoc) setEditTitle(selectedDoc.title);
  }, [selectedDoc]);

  return {
    documents,
    loading,
    search,
    setSearch,
    formatFilter,
    setFormatFilter,
    filters,
    setFilters,
    expandedSection,
    setExpandedSection,
    isStatsExpanded,
    setIsStatsExpanded,
    groupedDocs,
    filteredDocs,
    statsCounts,
    showUploadModal,
    setShowUploadModal,
    showCreateModal,
    setShowCreateModal,
    showPermissionModal,
    setShowPermissionModal,
    showPreviewModal,
    setShowPreviewModal,
    showDeleteModal,
    setShowDeleteModal,
    selectedDoc,
    setSelectedDoc,
    docToDeleteId,
    setDocToDeleteId,
    uploadFile,
    setUploadFile,
    uploadTitle,
    setUploadTitle,
    uploadCategory,
    setUploadCategory,
    isRelatedToCatalog,
    setIsRelatedToCatalog,
    uploadNotes,
    setUploadNotes,
    createType,
    setCreateType,
    createTitle,
    setCreateTitle,
    creating,
    uploading,
    deleting,
    editTitle,
    setEditTitle,
    editFile,
    setEditFile,
    excelData,
    allUsers,
    fetchDocuments,
    openDocument,
    handleDownloadFile,
    handleUpload,
    handleCreateBlank,
    handleDelete,
    confirmDelete,
    handleUpdate,
    handleUpdatePermissions,
    handleAddUser,
    canEdit,
    user,
    isExporting,
    handleExport,
    apiBaseUrl: API_BASE_URL,
  };
};
